import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import SectionWrapper from '../../CardRow/SectionWrapper';
import CustomTextField from '../../CustomizedComponents/CustomTextField';
import { useIntl } from 'react-intl';
import { BoldTitle } from '../../CustomizedComponents/Texts';
import DetailWrapper from '../../CardRow/DetailWrapper';
import DefaultButton from '../../Button/DefaultButton';
import ButtonWrapper from '../../Button/ButtonWrapper';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { createIdentifier } from '../../../utils/general';

const save = 'app.buttons.save';
const cancel = 'app.buttons.cancel';

const StyledPurchaseValueInputs = styled.span`
  display: inline-flex
`;

const priceSources = {
  bestAsk: 'BESTASK',
  bestBid: 'BESTBID',
  settlement: 'SETTLEMENT',
};

const getDataFromIndexValues = (indexValues, priceSource, tariffId) => {
  const found =
    indexValues &&
    indexValues?.find((item) => {
      return item.priceSource.id === priceSources[priceSource] && item.idTariff === tariffId;
    });

  return {
    id: found?.id || null,
    value: found?.value || 0,
    value2: found?.value2 || 0,
  };
};

const createIndexValues = (data, indexValues, purchaseIndexTypeId) => {
  const output = [];
  Object.keys(data).forEach((tariffKey) => {
    Object.keys(data[tariffKey]).forEach((propertyKey) => {
      output.push({
        id: getDataFromIndexValues(indexValues, propertyKey, parseInt(tariffKey)).id,
        priceSource: {
          id: priceSources[propertyKey],
        },
        purchaseIndexType: {
          id: purchaseIndexTypeId,
        },
        idTariff: parseInt(tariffKey),
        isSale: false,
        value: data[tariffKey][propertyKey].value,
        value2: data[tariffKey][propertyKey].value2,
      });
    });
  });

  return output;
};

const Parameters = ({ contract, handlers, editable, setEditable, enums, isNew, setOpen }) => {
  const {
    indexValues,
    isBeskAksAvailable: isBestAskAvailable,
    isSettlementAvailable,
    isSaleAvailable,
    commodityId,
    supplyProductId,
    purchaseIndexTypeId,
    postData,
    postLoadingContract,
    putLoadingContract,
  } = contract;

  const { sendHandler, setIndexValues } = handlers;

  const [data, setData] = useState({});
  const [indexValuesDefault, setIndexValuesDefault] = useState(null);

  const { handleSubmit, register, errors } = useForm();
  const intl = useIntl();
  const history = useHistory();

  const dataMissing = intl.formatMessage({ id: 'app.data.missing' });
  const dataIncorrect = intl.formatMessage({ id: 'app.data.incorrect' });

  useEffect(() => {
    if (!indexValuesDefault) {
      setIndexValuesDefault(indexValues);
    }
  }, [indexValues, indexValuesDefault]);

  useEffect(() => {
    postData && history.push(`/contracts/${postData.id}/basic-information`);
  }, [history, postData]);

  useEffect(() => {
    if (data) {
      setIndexValues(createIndexValues(data, indexValuesDefault, purchaseIndexTypeId));
    }
  }, [data, indexValuesDefault, purchaseIndexTypeId, setIndexValues]);

  useEffect(() => {
    const output = {};
    const tariffCount = enums.supplyProducts.find((product) => product.id === Number(supplyProductId))?.tariffCount;
    const combinationId = createIdentifier([isSettlementAvailable, commodityId === 'FIX', isBestAskAvailable, isSaleAvailable]);
    const fields = [];

    switch (combinationId) {
      case 2:
        fields.push('bestAsk');
        break;
      case 3:
        fields.push('bestAsk');
        fields.push('bestBid');
        break;
      case 6:
        fields.push('bestBid');
        break;
      case 7:
        fields.push('bestBid');
        fields.push('bestAsk');
        break;
      case 8:
        fields.push('settlement');
        break;
      case 9:
        fields.push('settlement');
        break;
      case 10:
        fields.push('settlement');
        fields.push('bestAsk');
        break;
      case 11:
        fields.push('settlement');
        fields.push('bestAsk');
        fields.push('bestBid');
        break;
      case 14:
        fields.push('settlement');
        fields.push('bestBid');
        break;
      case 15:
        fields.push('settlement');
        fields.push('bestBid');
        fields.push('bestAsk');
        break;
      default:
        break;
    }

    if (tariffCount) {
      for (let i = 1; i <= tariffCount; i++) {
        const tariff = {};
        fields.forEach((field) => {
          tariff[field] = getDataFromIndexValues(indexValuesDefault, field, i);
        });
        output[i] = tariff;
      }
      setData(output);
    }
  }, [supplyProductId, isBestAskAvailable, commodityId, isSaleAvailable, enums.supplyProducts, indexValuesDefault, isSettlementAvailable]);

  const handleClick = async (idButton) => {
    if (!isNew) {
      setEditable(!editable);
    }
    if (idButton !== 'cancel') {
      setOpen(true);
      sendHandler();
    }
  };

  return (
    <DetailWrapper>
      <form onSubmit={handleSubmit(handleClick)}>
        {data &&
          Object.keys(data).map((tariffKey) => (
            <SectionWrapper key={tariffKey}>
              <BoldTitle title={'app.contract.tariff'} text={tariffKey} />
              <div style={{ display: 'grid' }}>
                {Object.keys(data[tariffKey]).map((propertyKey) => (
                  <StyledPurchaseValueInputs key={`container-${propertyKey}`}>
                    {purchaseIndexTypeId !== 'RI+K' ? <CustomTextField
                      key={propertyKey}
                      name={`tariff[${tariffKey}][${propertyKey}]`}
                      label={intl.formatMessage({ id: `app.contract.${propertyKey}.label` })}
                      value={data[tariffKey][propertyKey].value.toString().replace('.', ',')}
                      variant="outlined"
                      disabled={!editable}
                      onChange={(event) => {
                        setData((oldData) => {
                          return {
                            ...oldData,
                            [tariffKey]: {
                              ...oldData[tariffKey],
                              [propertyKey]: {
                                ...oldData[tariffKey][propertyKey],
                                value: event.target.value,
                              },
                            },
                          };
                        });
                      }}
                      inputRef={register({
                        required: dataMissing,
                        pattern: {
                          value: /^[-+]?[0-9.,]+$/,
                          message: dataIncorrect,
                        },
                      })}
                      helperText={errors?.tariff?.[tariffKey]?.[propertyKey]?.message}
                    /> : null}
                    {purchaseIndexTypeId === 'RI+K' ? <CustomTextField
                      key={propertyKey}
                      name={`tariff[${tariffKey}][${propertyKey}]`}
                      label={intl.formatMessage({ id: `app.contract.${propertyKey}.ri.label` })}
                      value={data[tariffKey][propertyKey].value.toString().replace('.', ',')}
                      variant="outlined"
                      disabled={!editable}
                      onChange={(event) => {
                        setData((oldData) => {
                          return {
                            ...oldData,
                            [tariffKey]: {
                              ...oldData[tariffKey],
                              [propertyKey]: {
                                ...oldData[tariffKey][propertyKey],
                                value: event.target.value,
                              },
                            },
                          };
                        });
                      }}
                      inputRef={register({
                        required: dataMissing,
                        pattern: {
                          value: /^[-+]?[0-9.,]+$/,
                          message: dataIncorrect,
                        },
                      })}
                      helperText={errors?.tariff?.[tariffKey]?.[propertyKey]?.message}
                    /> : null}
                    {purchaseIndexTypeId === 'RI+K' ? <CustomTextField
                      key={`${propertyKey}-value2`}
                      name={`tariff[${tariffKey}][${propertyKey}].value2`}
                      label={intl.formatMessage({ id: `app.contract.${propertyKey}.value2.label` })}
                      value={data[tariffKey][propertyKey].value2.toString().replace('.', ',')}
                      variant="outlined"
                      disabled={!editable}
                      onChange={(event) => {
                        setData((oldData) => {
                          return {
                            ...oldData,
                            [tariffKey]: {
                              ...oldData[tariffKey],
                              [propertyKey]: {
                                ...oldData[tariffKey][propertyKey],
                                value2: event.target.value,
                              },
                            },
                          };
                        });
                      }}
                      inputRef={register({
                        required: dataMissing,
                        pattern: {
                          value: /^[-+]?[0-9.,]+$/,
                          message: dataIncorrect,
                        },
                      })}
                      helperText={errors?.tariff?.[tariffKey]?.[propertyKey]?.message}
                    /> : null}
                  </StyledPurchaseValueInputs>
                ))}
              </div>
            </SectionWrapper>
          ))}
        <ButtonWrapper>
          <DefaultButton
            id={'cancel'}
            primary={false}
            disabled={!editable}
            title={cancel}
            onClick={(event) => {
              handleClick(event.currentTarget.id);
            }}
          />
          <DefaultButton primary={true} disabled={!editable} type="submit" title={save} loaders={[postLoadingContract, putLoadingContract]} />
        </ButtonWrapper>
      </form>
    </DetailWrapper>
  );
};

Parameters.propTypes = {
  contract: PropTypes.object,
  handlers: PropTypes.object,
  editable: PropTypes.bool,
  setEditable: PropTypes.any,
  enums: PropTypes.object,
  isNew: PropTypes.bool,
  setOpen: PropTypes.func,
};

export default Parameters;
